/**
 * React Hook for Typing Effects
 * 
 * Integrates typing effects with Plate editor and manages
 * user preferences and localStorage persistence.
 */

import { useEffect, useRef, useCallback } from 'react';
import { PlateEditor } from '@udecode/plate/react';
import { TypingEffectsManager, TypingEffectsConfig, DEFAULT_TYPING_EFFECTS_CONFIG } from './typing-effects-manager';
import { useLocalStorage } from '@/hooks/useLocalStorage';

const STORAGE_KEY = 'plot-studio-typing-effects';

export interface UseTypingEffectsOptions {
  enabled?: boolean;
  autoInitialize?: boolean;
}

export interface UseTypingEffectsReturn {
  config: TypingEffectsConfig;
  updateConfig: (newConfig: Partial<TypingEffectsConfig>) => void;
  setEnabled: (enabled: boolean) => void;
  isInitialized: boolean;
  initialize: () => Promise<void>;
  manager: TypingEffectsManager | null;
  triggerEffects: () => void;
}

/**
 * Hook for managing typing effects in Plate editor
 */
export function useTypingEffects(
  editor: PlateEditor | null,
  options: UseTypingEffectsOptions = {}
): UseTypingEffectsReturn {
  const { enabled = true, autoInitialize = true } = options;

  const managerRef = useRef<TypingEffectsManager | null>(null);
  const isInitializedRef = useRef(false);
  const editorRef = useRef<PlateEditor | null>(null);

  // Load config from localStorage
  const { value: storedConfig, setValue: setStoredConfig } = useLocalStorage<TypingEffectsConfig>(
    STORAGE_KEY,
    DEFAULT_TYPING_EFFECTS_CONFIG
  );

  // Initialize manager with current config
  useEffect(() => {
    if (!managerRef.current) {
      managerRef.current = new TypingEffectsManager(storedConfig);
    } else {
      // Update existing manager with new config
      managerRef.current.updateConfig(storedConfig);
    }

    return () => {
      if (managerRef.current) {
        managerRef.current.dispose();
        managerRef.current = null;
      }
    };
  }, [storedConfig]);

  // Update editor reference
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Initialize when editor is ready
  const initialize = useCallback(async () => {
    if (!editor || !managerRef.current || isInitializedRef.current) {
      return;
    }

    try {
      await managerRef.current.initialize(editor);
      isInitializedRef.current = true;
    } catch (error) {
      console.error('Failed to initialize typing effects:', error);
    }
  }, [editor]);

  // Auto-initialize when editor is ready
  useEffect(() => {
    if (autoInitialize && editor && enabled) {
      // Small delay to ensure editor is fully ready
      const timer = setTimeout(initialize, 100);
      return () => clearTimeout(timer);
    }
  }, [editor, enabled, autoInitialize, initialize]);

  // Note: We're now using manual triggering from the Plate component's onChange
  // instead of hooking into the editor's onChange method directly

  // Update configuration
  const updateConfig = useCallback((newConfig: Partial<TypingEffectsConfig>) => {
    const updatedConfig = { ...storedConfig, ...newConfig };

    // Update localStorage
    setStoredConfig(updatedConfig);

    // Update manager
    if (managerRef.current) {
      managerRef.current.updateConfig(updatedConfig);
    }
  }, [storedConfig, setStoredConfig]);

  // Enable/disable effects
  const setEnabled = useCallback((isEnabled: boolean) => {
    if (managerRef.current) {
      managerRef.current.setEnabled(isEnabled);
    }
  }, []);

  // Manual trigger function
  const triggerEffects = useCallback(() => {
    if (managerRef.current && isInitializedRef.current) {
      managerRef.current.onTyping();
    }
  }, []);

  return {
    config: storedConfig,
    updateConfig,
    setEnabled,
    isInitialized: isInitializedRef.current,
    initialize,
    manager: managerRef.current,
    triggerEffects
  };
}

/**
 * Hook for typing effects preferences only (without editor integration)
 */
export function useTypingEffectsPreferences() {
  const { value: config, setValue: setConfig } = useLocalStorage<TypingEffectsConfig>(
    STORAGE_KEY,
    DEFAULT_TYPING_EFFECTS_CONFIG
  );

  const updateConfig = useCallback((newConfig: Partial<TypingEffectsConfig>) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
  }, [setConfig]);

  return {
    config,
    updateConfig
  };
}

/**
 * Hook for initializing typing effects on user interaction
 * Useful for handling browser autoplay policies
 */
export function useTypingEffectsInitializer(
  editor: PlateEditor | null,
  config: TypingEffectsConfig
) {
  const managerRef = useRef<TypingEffectsManager | null>(null);
  const isInitializedRef = useRef(false);

  const initializeOnInteraction = useCallback(async () => {
    if (!editor || isInitializedRef.current) return;

    try {
      if (!managerRef.current) {
        managerRef.current = new TypingEffectsManager(config);
      }

      await managerRef.current.initialize(editor);
      isInitializedRef.current = true;

      // Remove the event listeners after initialization
      document.removeEventListener('click', initializeOnInteraction);
      document.removeEventListener('keydown', initializeOnInteraction);
    } catch (error) {
      console.error('Failed to initialize typing effects on interaction:', error);
    }
  }, [editor, config]);

  useEffect(() => {
    if (editor && !isInitializedRef.current) {
      // Add event listeners for user interaction
      document.addEventListener('click', initializeOnInteraction, { once: true });
      document.addEventListener('keydown', initializeOnInteraction, { once: true });

      return () => {
        document.removeEventListener('click', initializeOnInteraction);
        document.removeEventListener('keydown', initializeOnInteraction);
      };
    }
  }, [editor, initializeOnInteraction]);

  useEffect(() => {
    return () => {
      if (managerRef.current) {
        managerRef.current.dispose();
      }
    };
  }, []);

  return {
    isInitialized: isInitializedRef.current,
    manager: managerRef.current
  };
}
